import { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  MobileStepper,
  Stack,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import Carousel from "../carousel/Carousel";

const STORAGE_KEY = "carouselPreviewSeen";

interface AssetUndercarriageIntroModalProps {
  enabled: boolean;
  assetId: string;
  previewImages: any[];
  ucImages: any[];
}

interface TourStep {
  eyebrow: string;
  title: string;
  body: string;
  mode: "exterior" | "uc";
  selector: string;
  spotlightSelector?: string;
  autoPlay?: boolean;
}

interface SpotlightRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

const AssetUndercarriageIntroModal: React.FC<AssetUndercarriageIntroModalProps> = ({
  enabled,
  assetId,
  previewImages,
  ucImages,
}) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [open, setOpen] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [spotlight, setSpotlight] = useState<SpotlightRect | null>(null);
  const carouselHostRef = useRef<HTMLDivElement | null>(null);
  const previousStepRef = useRef<TourStep | null>(null);
  const autoPlayStartedRef = useRef(false);

  const steps = useMemo(() => {
    const tourSteps: TourStep[] = [];

    if (previewImages.length > 0) {
      if (previewImages.length > 1) {
        tourSteps.push(
          {
            eyebrow: "Exterior Carousel",
            title: "Use play to auto-run through the exterior images.",
            body:
              "The play button starts the normal image flow so users can watch the photo set advance without clicking each frame.",
            mode: "exterior",
            selector: "[data-tour='carousel-play-button']",
            autoPlay: true,
          },
          {
            eyebrow: "Exterior Carousel",
            title: "Use the thumbnail strip to jump to a specific image.",
            body:
              "The thumbnails along the bottom let users pick a specific photo immediately instead of waiting for the carousel to cycle.",
            mode: "exterior",
            selector: "[data-tour='carousel-thumbnails']",
          }
        );
      }

      tourSteps.push({
        eyebrow: "Exterior Carousel",
        title: "Click the main image to open the zoom viewer.",
        body:
          "The main image itself is interactive. Clicking it opens the full-screen viewer so users can inspect the image more closely.",
        mode: "exterior",
        selector: "[data-tour='carousel-main-image']",
      });
    }

    if (ucImages.length > 1) {
      tourSteps.push({
        eyebrow: "Undercarriage Viewer",
        title: "Slide the scrubber to move through UC frames.",
        body:
          "The slider under the videos scrubs through the pass and updates both views together so users can compare the same frame side-by-side.",
        mode: "uc",
        selector: "[data-tour='uc-slider']",
        spotlightSelector: ".MuiSlider-root",
      });
    }

    tourSteps.push(
      {
        eyebrow: "Undercarriage Viewer",
        title: "Use audio to listen to the undercarriage pass.",
        body:
          "The speaker button starts or stops the synced audio track for the current UC sequence.",
        mode: "uc",
        selector: "[data-tour='uc-audio-button']",
        spotlightSelector: "button",
      },
      {
        eyebrow: "Undercarriage Viewer",
        title: "Capture downloads the current frame.",
        body:
          "The camera button exports the frame the user is looking at, which makes it easy to save a still for later review.",
        mode: "uc",
        selector: "[data-tour='uc-capture-button']",
        spotlightSelector: "button",
      }
    );

    if (ucImages.length > 1) {
      tourSteps.push({
        eyebrow: "Undercarriage Viewer",
        title: "Use the menu to switch layouts.",
        body:
          "The three-dot menu changes the UC layout between side-by-side, vertical, or a single-view mode.",
        mode: "uc",
        selector: "[data-tour='uc-layout-button']",
        spotlightSelector: "button",
      });
    }

    return tourSteps;
  }, [previewImages.length, ucImages.length]);

  const currentStep = steps[activeStep];

  useEffect(() => {
    if (!enabled || typeof window === "undefined") return;
    if (window.location.pathname.toLowerCase().includes("/iframe/")) return;

    try {
      const seen = window.localStorage.getItem(STORAGE_KEY);
      if (!seen) {
        setOpen(true);
        setActiveStep(0);
      }
    } catch (error) {
      console.warn("Unable to read carousel preview state", error);
      setOpen(true);
      setActiveStep(0);
    }
  }, [enabled]);

  useEffect(() => {
    if (!open) return;
    setSpotlight(null);

    const updateSpotlight = () => {
      const host = carouselHostRef.current;
      if (!host) return;

      const targetRoot = host.querySelector(currentStep.selector) as HTMLElement | null;
      const target = (currentStep.spotlightSelector
        ? targetRoot?.querySelector(currentStep.spotlightSelector)
        : targetRoot) as HTMLElement | null;

      const ucControlsReady = currentStep.selector !== "[data-tour='uc-slider']" ||
        !!host.querySelector("[data-tour='uc-audio-button'] button");

      if (!ucControlsReady) {
        setSpotlight(null);
        return;
      }

      if (!target) {
        setSpotlight(null);
        return;
      }

      const hostRect = host.getBoundingClientRect();
      const rect = target.getBoundingClientRect();
      const padding = currentStep.mode === "uc" ? 10 : 12;

      setSpotlight({
        top: rect.top - hostRect.top - padding,
        left: rect.left - hostRect.left - padding,
        width: rect.width + padding * 2,
        height: rect.height + padding * 2,
      });
    };

    const frame = window.requestAnimationFrame(updateSpotlight);
    const timeouts = [120, 320, 650, 1000].map((delay) => window.setTimeout(updateSpotlight, delay));
    window.addEventListener("resize", updateSpotlight);

    const observer = new ResizeObserver(() => updateSpotlight());
    if (carouselHostRef.current) {
      observer.observe(carouselHostRef.current);
    }

    return () => {
      window.cancelAnimationFrame(frame);
      timeouts.forEach((timeout) => window.clearTimeout(timeout));
      window.removeEventListener("resize", updateSpotlight);
      observer.disconnect();
    };
  }, [currentStep, open]);

  useEffect(() => {
    if (!open || !currentStep.autoPlay) return;

    const host = carouselHostRef.current;
    if (!host) return;

    const playButton = host.querySelector(currentStep.selector) as HTMLButtonElement | null;
    if (!playButton) return;

    const startTimer = window.setTimeout(() => {
      playButton.click();
      autoPlayStartedRef.current = true;
    }, 700);

    return () => {
      window.clearTimeout(startTimer);
    };
  }, [currentStep, open]);

  useEffect(() => {
    const previousStep = previousStepRef.current;
    if (!open || !previousStep?.autoPlay || currentStep.selector === previousStep.selector) {
      previousStepRef.current = currentStep;
      return;
    }

    const host = carouselHostRef.current;
    const playButton = host?.querySelector("[data-tour='carousel-play-button']") as HTMLButtonElement | null;
    if (playButton && autoPlayStartedRef.current) {
      playButton.click();
      autoPlayStartedRef.current = false;
    }

    previousStepRef.current = currentStep;
  }, [currentStep, open]);

  const closeModal = () => {
    try {
      window.localStorage.setItem(STORAGE_KEY, "true");
    } catch (error) {
      console.warn("Unable to persist carousel preview state", error);
    }
    setOpen(false);
  };

  if (!enabled || steps.length === 0) return null;

  const activeImages = currentStep.mode === "uc" ? ucImages : previewImages;
  const activeHeight = currentStep.mode === "uc" ? 510 : 370;
  const activeMinHeight = currentStep.mode === "uc" ? 470 : 330;

  return (
    <Dialog
      open={open}
      onClose={closeModal}
      fullWidth
      maxWidth="lg"
      fullScreen={fullScreen}
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 4,
          overflow: "hidden",
          background:
            "radial-gradient(circle at top, rgba(255,121,88,0.16), transparent 28%), linear-gradient(180deg, #10151c 0%, #0a0e14 100%)",
          color: "#fff",
        },
      }}
    >
      <DialogTitle sx={{ pb: 1, pr: 7 }}>
        <Typography
          sx={{
            fontSize: 13,
            letterSpacing: 1.4,
            textTransform: "uppercase",
            color: "#ff9b73",
            mb: 1,
          }}
        >
          {currentStep.eyebrow}
        </Typography>
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            lineHeight: 1.05,
            fontSize: { xs: "1.6rem", sm: "2.25rem" },
          }}
        >
          {currentStep.title}
        </Typography>
        <IconButton
          data-cy="uc-intro-close-btn"
          aria-label="Close carousel help"
          onClick={closeModal}
          sx={{ position: "absolute", top: 16, right: 16, color: "#fff" }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent sx={{ pt: 1, pb: 2 }}>
        <Stack spacing={3}>
          <Typography sx={{ color: "rgba(255,255,255,0.74)", maxWidth: 860 }}>
            {currentStep.body}
          </Typography>

          <Box
            ref={carouselHostRef}
            sx={{
              position: "relative",
              borderRadius: 3,
              border: "1px solid rgba(255,255,255,0.08)",
              background:
                "linear-gradient(180deg, rgba(22, 27, 34, 0.96) 0%, rgba(12, 16, 22, 0.98) 100%)",
              p: { xs: 1, sm: 1.5 },
              overflow: "hidden",
            }}
          >
            <Carousel
              key={currentStep.mode}
              images={activeImages}
              assetId={assetId}
              height={activeHeight}
              minHeight={activeMinHeight}
            />

            {spotlight && (
              <Box
                sx={{
                  position: "absolute",
                  top: spotlight.top,
                  left: spotlight.left,
                  width: spotlight.width,
                  height: spotlight.height,
                  borderRadius: currentStep.selector.includes("main-image") ? 4 : 2,
                  boxShadow: "0 0 0 9999px rgba(3, 7, 12, 0.72)",
                  border: "2px solid rgba(255,255,255,0.96)",
                  backgroundColor: "transparent",
                  pointerEvents: "none",
                  zIndex: 20,
                  transition:
                    "top 220ms ease, left 220ms ease, width 220ms ease, height 220ms ease",
                }}
              />
            )}
          </Box>

          <MobileStepper
            variant="dots"
            steps={steps.length}
            position="static"
            activeStep={activeStep}
            sx={{
              px: 0,
              bgcolor: "transparent",
              "& .MuiMobileStepper-dot": {
                width: 10,
                height: 10,
                bgcolor: "rgba(255,255,255,0.22)",
              },
              "& .MuiMobileStepper-dotActive": {
                bgcolor: "#ff6b5f",
              },
            }}
            nextButton={
              activeStep === steps.length - 1 ? (
                <Button
                  data-cy="uc-intro-start-btn"
                  variant="contained"
                  onClick={closeModal}
                  sx={{ borderRadius: 999, px: 2.5, bgcolor: "#d73f32" }}
                >
                  Start exploring
                </Button>
              ) : (
                <Button
                  data-cy="uc-intro-next-btn"
                  onClick={() => setActiveStep((step) => step + 1)}
                  endIcon={<KeyboardArrowRightIcon />}
                  sx={{ color: "#fff" }}
                >
                  Next
                </Button>
              )
            }
            backButton={
              <Button
                data-cy="uc-intro-back-btn"
                onClick={() => setActiveStep((step) => step - 1)}
                disabled={activeStep === 0}
                startIcon={<KeyboardArrowLeftIcon />}
                sx={{ color: "rgba(255,255,255,0.72)" }}
              >
                Back
              </Button>
            }
          />
        </Stack>
      </DialogContent>
    </Dialog>
  );
};

export default AssetUndercarriageIntroModal;
